type NestedRecord = string | number | boolean | null | NestedRecord[] | { [key: string]: NestedRecord };
type NestedObject = { [key: string]: NestedRecord };

/**
 * Reconstructs a nested object from flattened key-value pairs.
 * E.g., [{ key: "hero.title", value: "Hello", type: "text" }] => { hero: { title: "Hello" } }
 */
export function reconstructObject(entries: Array<{ key: string; value: string; type: string }>): NestedObject {
  const result: NestedObject = {};

  for (const entry of entries) {
    const keys = entry.key.split('.');
    let current: NestedObject = result;

    // Navigate/create nested structure
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!(key in current)) {
        current[key] = {};
      }
      current = current[key] as NestedObject;
    }

    // Set the final value
    const finalKey = keys[keys.length - 1];

    if (entry.type === 'json') {
      try {
        current[finalKey] = JSON.parse(entry.value);
      } catch {
        current[finalKey] = entry.value;
      }
    } else {
      current[finalKey] = entry.value;
    }
  }

  return convertToArrays(result) as NestedObject;
}

/**
 * Converts numeric-keyed objects to arrays recursively.
 * E.g., { "0": "a", "1": "b" } => ["a", "b"]
 */
export function convertToArrays(obj: unknown): unknown {
  if (typeof obj !== 'object' || obj === null) return obj;

  const keys = Object.keys(obj);
  const isArray = keys.length > 0 && keys.every(k => /^\d+$/.test(k));

  if (isArray) {
    const arr: unknown[] = [];
    for (const key of keys) {
      arr[parseInt(key)] = convertToArrays((obj as Record<string, unknown>)[key]);
    }
    return arr;
  }

  const result: Record<string, unknown> = {};
  for (const key of keys) {
    result[key] = convertToArrays((obj as Record<string, unknown>)[key]);
  }
  return result;
}

/**
 * Deep merge: overrides take precedence over defaults. Objects are merged recursively;
 * arrays and primitives are replaced wholesale.
 */
export function deepMerge<T>(defaults: T, overrides: Record<string, unknown>): T {
  const result: Record<string, unknown> = { ...(defaults as Record<string, unknown>) };
  for (const key of Object.keys(overrides)) {
    const defaultVal = (defaults as Record<string, unknown>)[key];
    const overrideVal = overrides[key];
    if (
      defaultVal != null &&
      overrideVal != null &&
      typeof defaultVal === 'object' &&
      typeof overrideVal === 'object' &&
      !Array.isArray(defaultVal) &&
      !Array.isArray(overrideVal)
    ) {
      result[key] = deepMerge(defaultVal, overrideVal as Record<string, unknown>);
    } else {
      result[key] = overrideVal;
    }
  }
  return result as T;
}
